/**
 * Admin — Funnel Inquiries
 * Companion to /admin/wellness-gateway-leads. Same UX language. Lists every
 * doc in db.funnel_inquiries, supports funnel_key / status filters + search,
 * inline status updates, and a side drawer for full inquiry details.
 *
 * Visibility: super_admin only (matches Wellness Gateway gating).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, adminAuth } from "@/lib/api";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Inbox, LogOut, Loader2, Search, ChevronLeft, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";

const FUNNEL_OPTIONS = [
  { value: "all", label: "All funnels" },
  { value: "university_pathway", label: "university_pathway" },
  { value: "coaching_funnel", label: "coaching_funnel" },
  { value: "kcis_clinical_assessment", label: "kcis_clinical_assessment" },
  { value: "comfort_care_inquiry", label: "comfort_care_inquiry" },
];
const STATUS_OPTIONS = [
  { value: "all", label: "All status" },
  { value: "new", label: "new" },
  { value: "contacted", label: "contacted" },
  { value: "qualified", label: "qualified" },
  { value: "converted", label: "converted" },
  { value: "archived", label: "archived" },
];

const STATUS_PILL = {
  new: "bg-slate-100 text-slate-700 border-slate-200",
  contacted: "bg-amber-50 text-amber-800 border-amber-200",
  qualified: "bg-blue-50 text-blue-700 border-blue-200",
  converted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  archived: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const FUNNEL_PRETTY = {
  university_pathway: "University",
  coaching_funnel: "Coaching",
  kcis_clinical_assessment: "KCIS Clinical Assessment",
  comfort_care_inquiry: "Comfort Care",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function InquiryDrawer({ inquiryId, open, onClose, onPatched }) {
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    if (!open || !inquiryId) return;
    let cancel = false;
    setLoading(true);
    api.get(`/admin/funnel-inquiries/${inquiryId}`)
      .then(({ data }) => { if (!cancel) { setInquiry(data); setNotes(data.notes || ""); } })
      .catch(() => toast.error("Could not load inquiry"))
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [open, inquiryId]);

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/admin/funnel-inquiries/${inquiryId}`, body);
      setInquiry(data);
      onPatched && onPatched(data);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(inquiry, null, 2));
      toast.success("Copied JSON");
    } catch { toast.error("Clipboard copy failed"); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="funnel-inquiry-drawer">
        {loading || !inquiry ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-purple-900" />
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="font-display text-2xl text-slate-900">{inquiry.first_name}</SheetTitle>
              <SheetDescription>
                <a href={`mailto:${inquiry.email}`} className="text-purple-900 underline">{inquiry.email}</a>
                <span className="text-slate-500"> · {fmtDate(inquiry.created_at)}</span>
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs">{FUNNEL_PRETTY[inquiry.funnel_key] || inquiry.funnel_key}</Badge>
              <span className={`text-xs rounded-full px-3 py-1 border ${STATUS_PILL[inquiry.status] || STATUS_PILL.new}`}>
                {inquiry.status}
              </span>
              {inquiry.source_lead_id && (
                <Badge variant="outline" className="text-xs">linked to wellness lead</Badge>
              )}
            </div>

            {inquiry.note && (
              <section className="mt-8">
                <div className="kcis-overline text-purple-900 mb-2">Their note</div>
                <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed bg-slate-50 rounded-lg p-3 border">
                  {inquiry.note}
                </p>
              </section>
            )}

            <section className="mt-8 kcis-card p-5">
              <div className="kcis-overline text-purple-900 mb-3">Follow-up</div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wide">Status</label>
                <Select
                  value={inquiry.status || "new"}
                  onValueChange={(v) => patch({ status: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="inquiry-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.filter(o => o.value !== "all").map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4">
                <label className="text-xs text-slate-500 uppercase tracking-wide">Internal notes</label>
                <textarea
                  className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-900"
                  rows={3}
                  value={notes}
                  data-testid="inquiry-notes"
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={async () => {
                    if (notes === (inquiry?.notes || "")) return;
                    setSavingNotes(true);
                    await patch({ notes });
                    setSavingNotes(false);
                  }}
                  placeholder="Internal follow-up notes…"
                />
                {savingNotes && <p className="text-xs text-slate-500 mt-1">Saving…</p>}
              </div>
              {inquiry.source_lead_id && (
                <div className="mt-4 text-xs text-slate-600">
                  Source lead:
                  {" "}
                  <Link to="/admin/wellness-gateway-leads" className="text-purple-900 underline">{inquiry.source_lead_id.slice(0, 8)}…</Link>
                </div>
              )}
            </section>

            <div className="mt-8 flex flex-wrap gap-3 sticky bottom-0 bg-white py-4 border-t">
              <Button variant="outline" onClick={copyJson} data-testid="copy-inquiry-json">
                <Copy className="h-4 w-4 mr-2" /> Copy JSON
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function AdminFunnelInquiries() {
  const navigate = useNavigate();
  const isSuper = adminAuth.isSuperAdmin();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(false);

  const [funnelKey, setFunnelKey] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [drawerId, setDrawerId] = useState(null);

  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    if (!isSuper) {
      toast.error("Super admin access required");
      navigate("/admin");
    }
  }, [navigate, isSuper]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (funnelKey !== "all") p.funnel_key = funnelKey;
    if (statusFilter !== "all") p.status = statusFilter;
    if (debouncedSearch) p.q = debouncedSearch;
    return p;
  }, [page, pageSize, funnelKey, statusFilter, debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/funnel-inquiries`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { setPage(1); }, [funnelKey, statusFilter, debouncedSearch]);
  useEffect(() => { load(); }, [load]);

  const updateRowStatus = async (id, status) => {
    try {
      await api.patch(`/admin/funnel-inquiries/${id}`, { status });
      setItems((prev) => prev.map(i => i.id === id ? { ...i, status } : i));
      toast.success(`Marked as ${status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  if (!isSuper) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav
        right={
          <div className="flex items-center gap-1">
            <Link to="/admin/dashboard">
              <Button variant="ghost" className="rounded-full text-slate-700">Dashboard</Button>
            </Link>
            <Link to="/admin/wellness-gateway-leads">
              <Button variant="ghost" className="rounded-full text-slate-700">Wellness Gateway</Button>
            </Link>
            <Button
              variant="ghost"
              className="rounded-full text-slate-700"
              onClick={() => { adminAuth.clear(); navigate("/"); }}
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        }
      />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        <div className="kcis-fade-up">
          <div className="kcis-overline text-purple-900 flex items-center gap-2">
            <Inbox className="h-4 w-4" /> Wellness Gateway
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-slate-900 mt-2">
            Funnel Inquiries
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Direct inquiries submitted through the four placeholder funnel pages
            (University, Coaching, KCIS Clinical Assessment, Comfort Care).
          </p>
        </div>

        {/* Filter bar */}
        <div className="mt-10 kcis-card p-5">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="funnel-inquiry-search"
                  className="pl-9"
                  placeholder="Name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="min-w-[200px]">
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">Funnel</label>
              <Select value={funnelKey} onValueChange={setFunnelKey}>
                <SelectTrigger data-testid="funnel-inquiry-funnel-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FUNNEL_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <label className="text-xs text-slate-500 uppercase tracking-wide block mb-1">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="funnel-inquiry-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 kcis-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="funnel-inquiries-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Funnel</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source lead</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="py-12 text-center text-slate-500">
                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
                  </TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="py-12 text-center text-slate-500">
                    No funnel inquiries match your filters yet.
                  </TableCell></TableRow>
                ) : items.map((it) => (
                  <TableRow key={it.id} className="hover:bg-purple-50/30" data-testid={`funnel-inquiry-row-${it.id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-slate-600">{fmtDate(it.created_at)}</TableCell>
                    <TableCell className="font-medium text-slate-900">{it.first_name}</TableCell>
                    <TableCell><a href={`mailto:${it.email}`} className="text-purple-900 underline">{it.email}</a></TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline">{FUNNEL_PRETTY[it.funnel_key] || it.funnel_key}</Badge>
                    </TableCell>
                    <TableCell>
                      <Select value={it.status || "new"} onValueChange={(v) => updateRowStatus(it.id, v)}>
                        <SelectTrigger className="h-8 w-32" data-testid={`inq-row-status-${it.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.filter(o => o.value !== "all").map(o => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {it.source_lead_id ? (
                        <Link to="/admin/wellness-gateway-leads" className="underline text-purple-900">
                          {it.source_lead_id.slice(0, 8)}…
                        </Link>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setDrawerId(it.id)} data-testid={`view-inquiry-${it.id}`}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between px-5 py-3 border-t bg-slate-50/40">
            <div className="text-xs text-slate-500">
              {total === 0 ? "0 inquiries" : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="inq-page-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-slate-600">Page {page} / {lastPage}</span>
              <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))} data-testid="inq-page-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </main>

      <InquiryDrawer
        inquiryId={drawerId}
        open={!!drawerId}
        onClose={() => setDrawerId(null)}
        onPatched={(patched) => setItems((prev) => prev.map(i => i.id === patched.id ? { ...i, ...patched } : i))}
      />

      <Footer />
    </div>
  );
}
